// Font business rules for napkin customization
import optionsConfig from './optionsConfig';

export interface FontConfig {
  name: string;
  size: string;
  maxChar: string;
  singleLine: string;
}

/**
 * Get the font configuration by font name
 */
export const getFontConfig = (fontName: string): FontConfig | undefined => {
  return optionsConfig.text.font.find(f => f.name === fontName);
};

/**
 * Check if the combination of font and image requires single line only
 */
export const shouldShowSingleLine = (fontName: string, imageName: string): boolean => {
  const fontConfig = getFontConfig(fontName);
  if (!fontConfig || !fontConfig.singleLine) return false;
  
  // Check if the current image is in the singleLine restriction list
  const restrictedImages = fontConfig.singleLine.split('|').map(img => img.toLowerCase());
  return restrictedImages.includes(imageName.toLowerCase());
};

/**
 * Calculate character count with capital letters counting as 2
 */
export const calculateCharacterCount = (text: string): number => {
  let count = 0;
  for (const char of text) {
    if (char >= 'A' && char <= 'Z') {
      count += 2; // Capital letters count as 2
    } else {
      count += 1;
    }
  }
  return count;
};

/**
 * Validate if text exceeds the maximum character limit for a font
 */
export const isWithinCharLimit = (text: string, fontName: string): boolean => {
  const fontConfig = getFontConfig(fontName);
  if (!fontConfig || !fontConfig.maxChar) return true;
  
  const maxChars = parseInt(fontConfig.maxChar, 10);
  const charCount = calculateCharacterCount(text);
  
  return charCount <= maxChars;
};

/**
 * Get the maximum character limit for a font
 */
export const getMaxCharLimit = (fontName: string): number => {
  const fontConfig = getFontConfig(fontName);
  if (!fontConfig || !fontConfig.maxChar) return 50; // Default limit
  
  return parseInt(fontConfig.maxChar, 10);
};

/**
 * Truncate text to fit within the character limit
 */
export const truncateToLimit = (text: string, fontName: string): string => {
  const maxChars = getMaxCharLimit(fontName);
  let charCount = 0;
  let truncatedText = '';
  
  for (const char of text) {
    const charWeight = (char >= 'A' && char <= 'Z') ? 2 : 1;
    if (charCount + charWeight > maxChars) {
      break;
    }
    charCount += charWeight;
    truncatedText += char;
  }
  
  return truncatedText;
};

/**
 * Get a warning message if text exceeds limit
 */
export const getCharLimitWarning = (text: string, fontName: string): string | null => {
  if (isWithinCharLimit(text, fontName)) return null;
  
  const fontConfig = getFontConfig(fontName);
  const charCount = calculateCharacterCount(text);
  const maxChars = fontConfig?.maxChar || '50';
  
  return `Text exceeds limit: ${charCount}/${maxChars} characters (capitals count as 2)`;
};
